import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import useSeo from "@/hooks/useSeo";

export default function LenderLogin() {
  useSeo({
    title: "Lender Login — SwiftCash Panel",
    description: "Approved SwiftCash lenders — sign in to see live UK business finance deals and place bids in real time.",
    path: "/lender/login",
  });
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "lender") return <Navigate to="/lender" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      if (u.role !== "lender") { toast.error("This is the lender portal. Admins log in at /admin/login."); return; }
      nav("/lender");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white text-ink min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-5 md:px-8 py-20">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-5">
          <Building2 className="h-3.5 w-3.5" strokeWidth={2.6} /> Lender panel
        </div>
        <h1 className="font-display font-extrabold text-[38px] md:text-[48px] leading-[1] tracking-[-0.035em] text-navy mb-8">
          Lender login
        </h1>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/60 mb-2 block">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="lender-login-email" className="h-12 rounded-xl font-medium" />
          </div>
          <div>
            <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/60 mb-2 block">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="lender-login-password" className="h-12 rounded-xl font-medium" />
          </div>
          <button type="submit" disabled={busy} data-testid="lender-login-submit"
            className="w-full h-12 rounded-xl bg-emerald text-white font-extrabold hover:bg-emerald-2 inline-flex items-center justify-center gap-2 text-[14px] disabled:opacity-50 shadow-emerald">
            {busy ? "Signing in…" : <>Sign in <ArrowRight className="h-4 w-4" strokeWidth={2.4} /></>}
          </button>
        </form>
        <div className="mt-8 text-center text-[13px] text-ink/60 font-medium">
          Not on the panel yet? <Link to="/lender/signup" className="text-emerald-3 font-bold hover:underline">Apply to join</Link>
        </div>
      </div>
    </div>
  );
}
